import { useState } from 'react';
import type { Address, AddressFormErrors } from '../types';
import { normalizePhone } from '@/hooks/useCustomerAuth';

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: Address) => void;
  initialAddress?: Address | null;
}

export default function AddressModal({ isOpen, onClose, onSave, initialAddress }: AddressModalProps) {
  const [addressForm, setAddressForm] = useState<Address>(initialAddress ?? {
    fullName: '',
    phone: '',
    address: '',
    city: 'Dehradun',
    pincode: '',
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(
    initialAddress?.lat && initialAddress?.lng ? { lat: initialAddress.lat, lng: initialAddress.lng } : null
  );
  const [locationDetected, setLocationDetected] = useState(!!(initialAddress?.lat && initialAddress?.lng));
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [manualSearch, setManualSearch] = useState('');
  const [manualSearchResults, setManualSearchResults] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]);
  const [manualSearchLoading, setManualSearchLoading] = useState(false);
  const [addressFormErrors, setAddressFormErrors] = useState<AddressFormErrors>({});

  if (!isOpen) return null;

  const handleDetectLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setMapCoords({ lat: latitude, lng: longitude });
        setLocationDetected(true);
        setAddressForm(prev => ({ ...prev, lat: latitude, lng: longitude }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          const addr = data.display_name || '';
          const city = data.address?.city || data.address?.town || data.address?.village || 'Dehradun';
          const pincode = data.address?.postcode || '';
          setAddressForm(prev => ({
            ...prev,
            address: data.address?.road ? `${data.address.road}${data.address.suburb ? ', ' + data.address.suburb : ''}` : prev.address,
            city,
            pincode: pincode || prev.pincode,
            mapAddress: addr,
            lat: latitude,
            lng: longitude,
          }));
        } catch { /* ignore */ }
        setLocationLoading(false);
      },
      (err) => {
        setLocationLoading(false);
        if (err.code === 1) {
          setLocationError('Location permission denied. Please allow location access and try again.');
        } else {
          setLocationError('Unable to detect location. Please enter address manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleManualSearch = async () => {
    if (!manualSearch.trim()) return;
    setManualSearchLoading(true);
    setManualSearchResults([]);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(manualSearch)}&format=json&limit=5&countrycodes=in`);
      const data = await res.json();
      setManualSearchResults(data);
    } catch {
      setManualSearchResults([]);
    }
    setManualSearchLoading(false);
  };

  const applyManualLocation = async (lat: number, lng: number, displayName: string) => {
    setMapCoords({ lat, lng });
    setLocationDetected(true);
    setShowManualLocation(false);
    setManualSearchResults([]);
    setManualSearch('');
    setAddressForm(prev => ({ ...prev, lat, lng, mapAddress: displayName }));
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      const city = data.address?.city || data.address?.town || data.address?.village || '';
      const pincode = data.address?.postcode || '';
      const road = data.address?.road ? `${data.address.road}${data.address.suburb ? ', ' + data.address.suburb : ''}` : '';
      setAddressForm(prev => ({
        ...prev,
        city: city || prev.city,
        pincode: pincode || prev.pincode,
        address: road || prev.address,
        mapAddress: displayName,
        lat,
        lng,
      }));
    } catch { /* ignore */ }
    if (addressFormErrors.location) {
      setAddressFormErrors(prev => ({ ...prev, location: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: AddressFormErrors = {};
    if (!addressForm.fullName.trim()) errors.fullName = 'Full name is required';
    if (!addressForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(addressForm.phone.replace(/\D/g, ''))) {
      errors.phone = 'Enter valid 10-digit phone number';
    }
    if (!addressForm.address.trim()) errors.address = 'Complete address is required';
    if (!addressForm.pincode.trim()) errors.pincode = 'Pincode is required';
    if (!mapCoords || !locationDetected) errors.location = 'Please pin your location — GPS se ya Search se koi bhi option use karo';

    if (Object.keys(errors).length > 0) {
      setAddressFormErrors(errors);
      return;
    }
    setAddressFormErrors({});

    const normalizedPhone = normalizePhone(addressForm.phone);
    const finalAddress: Address = { ...addressForm, phone: normalizedPhone };
    if (mapCoords) {
      finalAddress.lat = mapCoords.lat;
      finalAddress.lng = mapCoords.lng;
    }
    localStorage.setItem('userAddress', JSON.stringify(finalAddress));
    try {
      localStorage.setItem(`clothybazar_address_${normalizedPhone}`, JSON.stringify(finalAddress));
    } catch { /* ignore */ }

    onSave(finalAddress);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Delivery Address</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-4">Pin your location on map or enter address manually.</p>

        {/* Location Picker */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
              <i className="ri-map-pin-2-fill text-red-500"></i>
              Delivery Location
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button type="button" onClick={handleDetectLocation} disabled={locationLoading}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold hover:shadow-md transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap">
              {locationLoading ? <><i className="ri-loader-4-line animate-spin"></i>Detecting...</> : <><i className="ri-crosshair-2-line text-sm"></i>Use GPS Location</>}
            </button>
            <button type="button" onClick={() => { setShowManualLocation(p => !p); setManualSearchResults([]); setLocationError(''); }}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 text-xs font-semibold hover:border-gray-400 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-sm"></i>Search Location
            </button>
          </div>

          {showManualLocation && (
            <div className="mb-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                <i className="ri-search-line text-gray-500"></i>Apna area / mohalla / landmark dhundo
              </p>
              <div className="flex gap-2">
                <input type="text" value={manualSearch} onChange={e => setManualSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleManualSearch()}
                  placeholder="e.g. Rajpur Road Dehradun, Clock Tower..."
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs outline-none focus:border-green-500" />
                <button type="button" onClick={handleManualSearch} disabled={manualSearchLoading}
                  className="px-3 py-2 rounded-lg bg-gray-800 text-white text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap">
                  {manualSearchLoading ? <i className="ri-loader-4-line animate-spin"></i> : 'Search'}
                </button>
              </div>
              {manualSearchResults.length > 0 && (
                <div className="mt-2 space-y-1 max-h-44 overflow-y-auto">
                  {manualSearchResults.map((r, idx) => (
                    <button key={idx} type="button" onClick={() => applyManualLocation(parseFloat(r.lat), parseFloat(r.lon), r.display_name)}
                      className="w-full text-left px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all cursor-pointer">
                      <p className="text-xs text-gray-700 leading-relaxed line-clamp-2">
                        <i className="ri-map-pin-line text-green-600 mr-1"></i>{r.display_name}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {locationError && (
            <div className="mb-2 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0"></i>
              <div>
                <p className="text-red-600 text-xs">{locationError}</p>
                <button type="button" onClick={() => { setLocationError(''); setShowManualLocation(true); }}
                  className="text-xs text-green-600 font-semibold mt-1 underline cursor-pointer">
                  Search location manually instead
                </button>
              </div>
            </div>
          )}

          <div className="relative w-full rounded-xl overflow-hidden border border-gray-200" style={{ height: '200px' }}>
            {mapCoords ? (
              <>
                <iframe title="Delivery Location" width="100%" height="200" style={{ border: 0 }} loading="lazy" allowFullScreen
                  src={`https://maps.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=16&output=embed`}></iframe>
                <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1 shadow">
                  <i className="ri-map-pin-fill"></i>Location Pinned ✓
                </div>
                <button type="button" onClick={() => { setMapCoords(null); setLocationDetected(false); setAddressForm(prev => ({ ...prev, lat: undefined, lng: undefined, mapAddress: '' })); }}
                  className="absolute top-2 right-2 bg-white text-gray-600 text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 shadow hover:bg-gray-100 cursor-pointer">
                  <i className="ri-refresh-line"></i> Change
                </button>
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow">
                  <i className="ri-map-2-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 text-sm text-center px-4">
                  GPS button se ya <span className="font-semibold text-green-600">Search</span> se location pin karo
                </p>
              </div>
            )}
          </div>

          {addressForm.mapAddress && (
            <div className="mt-2 p-2 rounded-lg bg-green-50 border border-green-200 flex items-start gap-2">
              <i className="ri-map-pin-line text-green-600 mt-0.5 shrink-0"></i>
              <p className="text-green-700 text-xs leading-relaxed">{addressForm.mapAddress}</p>
            </div>
          )}
        </div>

        {addressFormErrors.location && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 border-2 border-red-300 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shrink-0">
              <i className="ri-map-pin-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-red-700 font-bold text-sm">Location Required!</p>
              <p className="text-red-600 text-xs mt-1">{addressFormErrors.location}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
            <input type="text" value={addressForm.fullName}
              onChange={(e) => { setAddressForm({ ...addressForm, fullName: e.target.value }); if (addressFormErrors.fullName) setAddressFormErrors({ ...addressFormErrors, fullName: undefined }); }}
              className={`w-full px-4 py-3 rounded-xl border outline-none text-sm transition-all ${addressFormErrors.fullName ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'}`}
              placeholder="Enter your full name" />
            {addressFormErrors.fullName && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{addressFormErrors.fullName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
            <input type="tel" value={addressForm.phone}
              onChange={(e) => { setAddressForm({ ...addressForm, phone: e.target.value }); if (addressFormErrors.phone) setAddressFormErrors({ ...addressFormErrors, phone: undefined }); }}
              className={`w-full px-4 py-3 rounded-xl border outline-none text-sm transition-all ${addressFormErrors.phone ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'}`}
              placeholder="9876543210" />
            {addressFormErrors.phone && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{addressFormErrors.phone}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Complete Address <span className="text-red-500">*</span></label>
            <textarea value={addressForm.address}
              onChange={(e) => { setAddressForm({ ...addressForm, address: e.target.value }); if (addressFormErrors.address) setAddressFormErrors({ ...addressFormErrors, address: undefined }); }}
              className={`w-full px-4 py-3 rounded-xl border outline-none resize-none text-sm transition-all ${addressFormErrors.address ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'}`}
              rows={2} placeholder="House no., Street, Landmark" />
            {addressFormErrors.address && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{addressFormErrors.address}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none text-sm" placeholder="City" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode <span className="text-red-500">*</span></label>
              <input type="text" value={addressForm.pincode}
                onChange={(e) => { setAddressForm({ ...addressForm, pincode: e.target.value }); if (addressFormErrors.pincode) setAddressFormErrors({ ...addressFormErrors, pincode: undefined }); }}
                className={`w-full px-4 py-3 rounded-xl border outline-none text-sm transition-all ${addressFormErrors.pincode ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'}`}
                placeholder="248001" />
              {addressFormErrors.pincode && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{addressFormErrors.pincode}</p>}
            </div>
          </div>

          <div className={`p-3 rounded-xl border flex items-center gap-3 ${locationDetected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${locationDetected ? 'bg-green-500' : 'bg-amber-500'}`}>
              <i className={`${locationDetected ? 'ri-map-pin-fill' : 'ri-map-pin-line'} text-white text-lg`}></i>
            </div>
            <div className="flex-1">
              <p className={`font-semibold text-sm ${locationDetected ? 'text-green-700' : 'text-amber-700'}`}>
                {locationDetected ? 'Location Pinned ✓' : 'Location Required'}
              </p>
              <p className={`text-xs ${locationDetected ? 'text-green-600' : 'text-amber-600'}`}>
                {locationDetected ? 'Delivery location set hai' : 'GPS ya Search — kisi bhi option se location pin karo'}
              </p>
            </div>
            {!locationDetected && <span className="text-amber-500 text-2xl">!</span>}
          </div>

          <button type="submit" disabled={!locationDetected}
            className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer ${locationDetected ? 'bg-gradient-to-r from-primary-purple to-primary-pink text-white hover:shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
            <i className="ri-map-pin-add-fill"></i>
            {locationDetected ? 'Save Address & Continue' : 'Pehle Location Pin Karo'}
          </button>
        </form>
      </div>
    </div>
  );
}
